<script setup lang="ts">
import { ref, computed } from 'vue';
import IconsImageStyleFeature from '../../icons/ImageStyleFeature.vue';
import ImageLazy from '@/components/ImageLazy.vue';
import { t } from '~/i18n';

const imageStyleDefs = [
  {
    i18nKey: 'painting',
    image:
      'https://static.seedeai.com/asset/9d2ea207-8755-4621-9ea1-d7b465fb1331?x-oss-process=style/webp',
  },
  { i18nKey: 'realistic', image: '/images/imageStyle/Realistic.webp' },
  { i18nKey: 'natural', image: '/images/imageStyle/natural.webp' },
  { i18nKey: 'cyberpunk', image: '/images/imageStyle/Cyberpunk.webp' },
  { i18nKey: 'neon', image: '/images/imageStyle/Neon.webp' },
  { i18nKey: 'futuristic', image: '/images/imageStyle/futuristic.webp' },
  { i18nKey: 'magic', image: '/images/imageStyle/magic.webp' },
  { i18nKey: '3d_surrealism', image: '/images/imageStyle/3DfuzzySurrealism.webp' },
  { i18nKey: 'line_art', image: '/images/imageStyle/LineArt.webp' },
  { i18nKey: 'webcomic', image: '/images/imageStyle/Webcomic.webp' },
  { i18nKey: 'narrative_comic', image: '/images/imageStyle/narrativeComic.webp' },
  { i18nKey: 'neo_folk_pop', image: '/images/imageStyle/Neo-FolkPop.webp' },
  { i18nKey: 'halftone', image: '/images/imageStyle/Halftone.webp' },
  { i18nKey: 'old_halftone', image: '/images/imageStyle/oldhalftone.webp' },
  { i18nKey: 'surreal_arch', image: '/images/imageStyle/Fine-lineSurrealArchitectural.webp' },
] as const;

const imageStyles = computed(() =>
  imageStyleDefs.map((def) => ({
    ...def,
    name: t(`landing_home.cards.image_styles.${def.i18nKey}`),
  }))
);
const activeStyleIndex = ref(0);
const styleWheelRef = ref<HTMLElement | null>(null);
const wheelOffset = ref(0);
let wheelScrollTimeout: ReturnType<typeof setTimeout> | null = null;
let wheelAccumulator = 0;
const WHEEL_THRESHOLD = 50;

const handleStyleWheel = (event: WheelEvent) => {
  // Only handle horizontal scroll, let vertical scroll pass through for page scrolling
  if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) {
    // Vertical scroll - don't prevent default, let page scroll
    return;
  }

  event.preventDefault();
  const delta = event.deltaX;
  wheelAccumulator += delta;
  wheelOffset.value = Math.max(-20, Math.min(20, wheelAccumulator * 0.2));

  if (Math.abs(wheelAccumulator) >= WHEEL_THRESHOLD) {
    const direction = wheelAccumulator > 0 ? 1 : -1;
    const len = imageStyles.value.length;
    activeStyleIndex.value = (activeStyleIndex.value + direction + len) % len;
    wheelAccumulator = 0;
    wheelOffset.value = direction * -8;
    setTimeout(() => {
      wheelOffset.value = 0;
    }, 20);
  }

  if (wheelScrollTimeout) clearTimeout(wheelScrollTimeout);
  wheelScrollTimeout = setTimeout(() => {
    wheelAccumulator = 0;
    wheelOffset.value = 0;
  }, 150);
};

// Navigate to next/previous style
const navigateStyle = (direction: number) => {
  const len = imageStyles.value.length;
  activeStyleIndex.value = (activeStyleIndex.value + direction + len) % len;
};

const getVisibleStyles = () => {
  const len = imageStyles.value.length;
  const visibleCount = 5;
  const halfVisible = Math.floor(visibleCount / 2);
  const result = [];
  for (let i = -halfVisible; i <= halfVisible; i++) {
    const actualIndex = (activeStyleIndex.value + i + len) % len;
    result.push({ ...imageStyles.value[actualIndex], actualIndex, offset: i });
  }
  return result;
};

const getStyleOpacity = (offset: number) => {
  const distance = Math.abs(offset);
  if (distance === 0) return 1;
  if (distance === 1) return 0.5;
  return 0.25;
};

const getStyleScale = (offset: number) => {
  const distance = Math.abs(offset);
  if (distance === 0) return 1;
  if (distance === 1) return 0.85;
  return 0.7;
};
</script>

<template>
  <div
    ref="styleWheelRef"
    class="group relative rounded-3xl border border-black/5 overflow-hidden cursor-ew-resize"
    @wheel="handleStyleWheel"
  >
    <!-- Background image - fills entire card -->
    <div class="absolute inset-0">
      <TransitionGroup name="style-fade">
        <ImageLazy
          v-for="(style, index) in imageStyles"
          v-show="index === activeStyleIndex"
          :key="style.i18nKey"
          :src="style.image"
          :alt="`${style.name} style preview`"
          class="absolute inset-0 w-full h-full"
          image-class="w-full h-full object-cover"
        />
      </TransitionGroup>
      <!-- Gradient overlay for text readability -->
      <div
        class="absolute inset-0 pointer-events-none"
        style="
          background: linear-gradient(
            to bottom,
            rgba(0, 0, 0, 0.4) 0%,
            rgba(0, 0, 0, 0.2) 40%,
            transparent 50%,
            rgba(0, 0, 0, 0.2) 80%,
            rgba(0, 0, 0, 0.5) 100%
          );
        "
      />
    </div>
    <!-- Content overlay -->
    <div class="relative z-10 flex flex-col h-full min-h-[400px]">
      <!-- Top: Title and description -->
      <div class="p-6 md:p-8">
        <IconsImageStyleFeature class="w-5 h-5 text-white mb-4" />
        <p class="text-lg md:text-xl leading-[1.4]">
          <span class="text-white">{{ $t('landing_home.cards.image_styles.primary') }}</span>
          <span class="text-white/60">{{ $t('landing_home.cards.image_styles.secondary') }}</span>
        </p>
      </div>
      <!-- Bottom: Horizontal Style selector -->
      <div class="flex-1 flex items-end">
        <div class="w-full pb-6 md:pb-8 px-4 md:px-6">
          <div class="flex items-center justify-center gap-1">
            <!-- Left arrow -->
            <button
              class="w-8 h-8 flex items-center justify-center text-white/60 hover:text-white transition-colors flex-shrink-0"
              @click="navigateStyle(-1)"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <!-- Style labels container -->
            <div class="flex-1 overflow-hidden">
              <div
                class="flex items-center justify-center gap-4 transition-transform select-none"
                :style="{
                  transform: `translateX(${wheelOffset}px)`,
                  transitionDuration: wheelOffset === 0 ? '200ms' : '50ms',
                  transitionTimingFunction:
                    wheelOffset === 0 ? 'cubic-bezier(0.34, 1.56, 0.64, 1)' : 'linear',
                }"
              >
                <span
                  v-for="item in getVisibleStyles()"
                  :key="`${item.i18nKey}-${item.offset}`"
                  class="text-xs md:text-sm font-medium tracking-wider transition-all duration-200 cursor-pointer hover:opacity-90 whitespace-nowrap px-2 py-1"
                  :class="{ 'font-semibold': item.offset === 0 }"
                  :style="{
                    opacity: getStyleOpacity(item.offset),
                    transform: `scale(${getStyleScale(item.offset)})`,
                    color: 'white',
                  }"
                  @click="activeStyleIndex = item.actualIndex"
                >
                  {{ item.name }}
                </span>
              </div>
            </div>
            <!-- Right arrow -->
            <button
              class="w-8 h-8 flex items-center justify-center text-white/60 hover:text-white transition-colors flex-shrink-0"
              @click="navigateStyle(1)"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.style-fade-enter-active,
.style-fade-leave-active {
  transition: opacity 0.4s ease;
}
.style-fade-enter-from,
.style-fade-leave-to {
  opacity: 0;
}
</style>
